import type {
  MaintenanceCostSplit,
  MaintenanceOwnerShare,
  MaintenanceRecord,
} from '../types';
import { formatPercent } from './formatMetric';
import { formatOwnershipShare } from './ownershipShare';

export const COST_SPLIT_OPTIONS: {
  value: MaintenanceCostSplit;
  label: string;
}[] = [
  { value: 'hours', label: 'Согласно налёту' },
  { value: 'shares', label: 'Согласно текущим долям' },
  { value: 'manual', label: 'Вручную' },
];

export const resolveCostSplit = (
  record: Pick<MaintenanceRecord, 'cost_split' | 'pilot_data'> | null | undefined
): MaintenanceCostSplit => {
  const raw = record?.cost_split || record?.pilot_data?.split;
  if (raw === 'manual' || raw === 'shares') return raw;
  return 'hours';
};

export const costSplitTitle = (split: MaintenanceCostSplit) =>
  COST_SPLIT_OPTIONS.find((item) => item.value === split)?.label ||
  'Согласно налёту';

export const formatRub = (value: number) =>
  `${value.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ₽`;

export const parseOptionalCost = (
  value: string
): { cost: number | null; error?: string } => {
  const text = value.trim().replace(',', '.');
  if (text === '') return { cost: null };
  const cost = Number(text);
  if (!Number.isFinite(cost) || cost < 0) {
    return { cost: null, error: 'Укажите стоимость в рублях' };
  }
  return { cost };
};

const allocateByPercent = (percents: number[], total: number) => {
  let allocated = 0;
  return percents.map((percent, index) => {
    if (index === percents.length - 1) {
      return Math.round((total - allocated) * 100) / 100;
    }
    const share = Math.round(((total * (percent || 0)) / 100) * 100) / 100;
    allocated += share;
    return share;
  });
};

export type CostSplitRow = {
  key: string;
  name: string;
  detail: string;
  cost: number | null;
};

const parseCost = (value: string | number | null | undefined) => {
  if (value == null || value === '') return null;
  const total = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(total) || total < 0) return null;
  return total;
};

export const costSplitRows = (
  record: MaintenanceRecord,
  costSource?: string | number | null,
  split: MaintenanceCostSplit = resolveCostSplit(record)
): CostSplitRow[] => {
  const total = parseCost(
    costSource ?? (record.cost != null && record.cost > 0 ? record.cost : null)
  );

  if (split === 'shares') {
    const owners = record.owners ?? [];
    const amounts = total == null ? [] : allocateByPercent(
      owners.map((owner) => owner.percent || 0),
      total
    );
    return owners.map((owner: MaintenanceOwnerShare, index) => ({
      key: String(owner.user_id || owner.name || index),
      name: owner.name,
      detail: formatOwnershipShare(owner.share),
      cost: amounts[index] ?? null,
    }));
  }

  if (split === 'manual') {
    const saved = record.pilot_data?.allocations || [];
    const owners = record.owners ?? [];
    const nameById = new Map(owners.map((owner) => [owner.user_id, owner.name]));
    const rows = saved.filter((item) => (item.share || 0) > 0 || (item.amount || 0) > 0);
    const shareSum = rows.reduce((acc, row) => acc + (row.share || 0), 0);
    const amounts =
      total == null
        ? []
        : allocateByPercent(
            rows.map((item) =>
              shareSum > 0 ? ((item.share || 0) / shareSum) * 100 : 0
            ),
            total
          );
    return rows.map((item, index) => ({
      key: String(item.user_id || index),
      name: nameById.get(item.user_id) || `Участник ${item.user_id}`,
      detail: formatOwnershipShare(
        shareSum > 0 ? (item.share || 0) / shareSum : 0
      ),
      cost: amounts[index] ?? item.amount ?? null,
    }));
  }

  const pilots = record.pilot_data?.pilots ?? [];
  const amounts = total == null ? [] : allocateByPercent(
    pilots.map((pilot) => pilot.percent || 0),
    total
  );
  return pilots.map((pilot, index) => ({
    key: `${pilot.pilot}-${index}`,
    name: pilot.pilot,
    detail: `${pilot.hours} м/ч (${formatPercent(pilot.percent)}%)`,
    cost: amounts[index] ?? pilot.cost ?? null,
  }));
};
